import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { usePropertyDetailQuery } from "@/hooks/usePropertyDetailQuery";
import { queryKeys } from "@/hooks/queryKeys";
import { useUiStore } from "@/stores/ui.store";
import { RentStatusBadge } from "@/components/RentStatusBadge";
import { Button } from "@/components/ui/Button";
import { Card, CardHeader, CardContent } from "@/components/ui/Card";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Typography } from "@/components/ui/Typography";
import { formatNextDueDate } from "@/lib/dateUtils";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const PAYMENT_MODES = ["CASH", "UPI", "BANK_TRANSFER", "CHEQUE", "OTHER"] as const;

export function PropertyDetailPage() {
  const params = useParams();
  const id = params.id as string;
  const queryClient = useQueryClient();
  const { property, rentRecords, expenses, loading, error, refetch } = usePropertyDetailQuery(id);
  const addToast = useUiStore((s) => s.addToast);

  const markPaidMutation = useMutation({
    mutationFn: ({ rentId, paymentMode, notes }: { rentId: string; paymentMode: string; notes?: string }) =>
      api.patch(`/rent/${rentId}/mark-paid`, { paymentMode, notes }),
    onSuccess: () => {
      addToast({ type: "success", message: "Rent marked as paid" });
      setMarkPaidModal(null);
      setNotes("");
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyDetail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.properties() });
      refetch();
    },
    onError: (err: Error) => {
      addToast({ type: "error", message: err instanceof ApiError ? err.message : "Failed to update" });
    },
  });

  const [markPaidModal, setMarkPaidModal] = useState<{
    rentId: string;
    month: number;
    year: number;
  } | null>(null);
  const [addRentModal, setAddRentModal] = useState(false);
  const [addRentUnitId, setAddRentUnitId] = useState("");
  const [addRentMonth, setAddRentMonth] = useState(String(new Date().getMonth() + 1));
  const [addRentYear, setAddRentYear] = useState(String(new Date().getFullYear()));
  const [paymentMode, setPaymentMode] = useState<typeof PAYMENT_MODES[number]>("UPI");
  const [notes, setNotes] = useState("");

  const addRentMutation = useMutation({
    mutationFn: (body: { roomId: string; month: number; year: number }) =>
      api.post("/rent", body),
    onSuccess: () => {
      addToast({ type: "success", message: "Rent record added" });
      setAddRentModal(false);
      setAddRentUnitId("");
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyDetail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.properties() });
      refetch();
    },
    onError: (err: Error) => {
      addToast({ type: "error", message: err instanceof ApiError ? err.message : "Failed to add rent record" });
    },
  });

  function handleMarkPaid() {
    if (!markPaidModal) return;
    markPaidMutation.mutate({
      rentId: markPaidModal.rentId,
      paymentMode,
      notes: notes.trim() || undefined,
    });
  }

  const submitting = markPaidMutation.isPending;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse" />
        <div className="h-48 bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse" />
        <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="space-y-6">
        <Link
          to="/properties"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
          Back to properties
        </Link>
        <div className="rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6 text-center">
          <p className="text-red-700 dark:text-red-300 font-medium">{error ?? "Property not found"}</p>
          <Link to="/properties" className="mt-4 inline-block text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300">
            Back to properties
          </Link>
        </div>
      </div>
    );
  }

  const now = new Date();
  const currentDay = now.getDate();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const getStatus = (status: string, dueDate: number) => {
    if (status === "PAID") return "PAID";
    if (status === "AWAITING_VERIFICATION") return "AWAITING_VERIFICATION";
    if (status === "PENDING" && currentDay > dueDate) return "OVERDUE";
    return "PENDING";
  };

  /** When rent is PENDING and for current month, show "Due in X days" when within 5 days of due date. */
  function getRentDueLabel(
    record: { status: string; month: number; year: number },
    dueDate: number
  ): string | null {
    if (record.status !== "PENDING" || record.month !== currentMonth || record.year !== currentYear)
      return null;
    const due = new Date(currentYear, currentMonth - 1, dueDate);
    const today = new Date(currentYear, currentMonth - 1, currentDay);
    const daysUntil = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (daysUntil > 5) return null;
    if (daysUntil > 1) return `Due in ${daysUntil} days`;
    if (daysUntil === 1) return "Due tomorrow";
    if (daysUntil === 0) return "Due today";
    return null; // overdue shown as status
  }

  const units = property.units ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <Link
            to="/properties"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 mb-2"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
            Back to properties
          </Link>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Typography variant="h1">{property.name}</Typography>
              <p className="mt-1 text-slate-500 dark:text-slate-400">{property.address}</p>
            </div>
            <Link
              to={`/property/${property.id}/edit`}
              className="sm:hidden shrink-0 rounded-lg p-2.5 border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-500 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Edit property"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
              </svg>
            </Link>
          </div>
        </div>
        <Link to={`/property/${property.id}/edit`} className="hidden sm:inline-block">
          <Button variant="secondary" size="sm">
            Edit
          </Button>
        </Link>
      </div>

      {/* Property summary */}
      <Card>
        <CardContent className="flex flex-wrap gap-6 py-5 sm:py-6">
          <div>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Total monthly rent</p>
            <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
              ₹{property.totalRent.toLocaleString("en-IN")}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Units</p>
            <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">{units.length}</p>
          </div>
        </CardContent>
      </Card>

      {/* Units list */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Typography variant="h2">Units</Typography>
            <Link to={`/property/${property.id}/units/new`}>
              <Button size="sm">+ Add unit</Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {units.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400 py-4">No units yet.</p>
            ) : (
              units.map((unit) => (
                <Link
                  key={unit.id}
                  to={`/property/${property.id}/unit/${unit.id}`}
                  className="block rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-700/30 p-4 sm:p-5 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:border-slate-200 dark:hover:border-slate-600 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        {unit.alias} ({unit.unitNumber}) · {unit.unitType.replace("_", " ")}
                      </p>
                      <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mt-1">
                        ₹{unit.rentAmount.toLocaleString("en-IN")}/month · Due {unit.dueDate}th · Next due {formatNextDueDate(unit.dueDate)} · {unit.tenants.length}/{unit.maxTenants} tenants
                      </p>
                      {(unit.securityDeposit != null || unit.agreementEndDate) && (
                        <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {unit.securityDeposit != null && (
                            <span>Deposit: ₹{unit.securityDeposit.toLocaleString("en-IN")}</span>
                          )}
                          {unit.agreementEndDate && (
                            <span>Agreement ends: {new Date(unit.agreementEndDate).toLocaleDateString("en-IN")}</span>
                          )}
                        </div>
                      )}
                      {unit.notes && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{unit.notes}</p>
                      )}
                    </div>
                    <span className="text-slate-400 dark:text-slate-500 shrink-0">
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m9 5 7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                  {unit.tenants.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Tenants</p>
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        {unit.tenants.map((t) => t.name).join(", ")}
                      </p>
                    </div>
                  )}
                </Link>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Rent records – only when property has units */}
      {units.length > 0 && (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Typography variant="h2">Rent records ({new Date().getFullYear()})</Typography>
            <Button size="sm" variant="secondary" onClick={() => setAddRentModal(true)}>
              Add rent record (back date)
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {rentRecords.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400 py-4">No rent records yet. They are created automatically each month.</p>
          ) : (
            <>
              {/* Mobile: card layout */}
              <div className="space-y-3 md:hidden">
                {rentRecords.map((record) => {
                  const status = getStatus(record.status, record.unitDueDate ?? record.roomDueDate ?? 1);
                  return (
                    <div
                      key={record.id}
                      className="rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-700/30 p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-900 dark:text-slate-100">
                            {record.unitAlias ?? record.roomAlias ?? record.unitNumber ?? record.roomNumber}
                          </p>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">
                            {MONTHS[record.month - 1]} {record.year}
                          </p>
                          <p className="text-base font-semibold text-slate-900 dark:text-slate-100 mt-2">
                            ₹{record.amount.toLocaleString("en-IN")}
                          </p>
                        </div>
                        <div className="shrink-0 flex flex-col items-end gap-2">
                          {getRentDueLabel(record, record.unitDueDate ?? record.roomDueDate ?? 1) && (
                            <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                              {getRentDueLabel(record, record.unitDueDate ?? record.roomDueDate ?? 1)}
                            </span>
                          )}
                          <RentStatusBadge status={status as "PENDING" | "PAID" | "AWAITING_VERIFICATION" | "OVERDUE"} />
                          {record.status === "PENDING" && (
                            <button
                              onClick={() =>
                                setMarkPaidModal({
                                  rentId: record.id,
                                  month: record.month,
                                  year: record.year,
                                })
                              }
                              className="rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2.5 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-500 active:scale-[0.98] min-h-[44px] flex items-center justify-center gap-2"
                            >
                              <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                              Mark paid
                            </button>
                          )}
                          {record.status === "PAID" && record.paidDate && (
                            <span className="text-slate-500 dark:text-slate-400 text-xs">
                              Paid {new Date(record.paidDate).toLocaleDateString("en-IN")}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop: table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
<thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-3 font-medium text-slate-500 dark:text-slate-400">Unit</th>
                    <th className="text-left py-3 font-medium text-slate-500 dark:text-slate-400">Month</th>
                    <th className="text-left py-3 font-medium text-slate-500 dark:text-slate-400">Amount</th>
                    <th className="text-left py-3 font-medium text-slate-500 dark:text-slate-400">Status</th>
                    <th className="text-right py-3 font-medium text-slate-500 dark:text-slate-400">Action</th>
                  </tr>
                </thead>
                <tbody>
                    {rentRecords.map((record) => {
                      const status = getStatus(record.status, record.unitDueDate ?? record.roomDueDate ?? 1);
                      return (
                        <tr key={record.id} className="border-b border-slate-100 dark:border-slate-700 last:border-0">
                          <td className="py-3 font-medium text-slate-900 dark:text-slate-100">
                            {record.unitAlias ?? record.roomAlias ?? record.unitNumber ?? record.roomNumber}
                          </td>
                          <td className="py-3 font-medium text-slate-900 dark:text-slate-100">
                            {MONTHS[record.month - 1]} {record.year}
                          </td>
                          <td className="py-3 text-slate-700 dark:text-slate-300">
                            ₹{record.amount.toLocaleString("en-IN")}
                          </td>
                          <td className="py-3">
                            <div className="flex flex-col gap-0.5">
                              {getRentDueLabel(record, record.unitDueDate ?? record.roomDueDate ?? 1) && (
                                <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                                  {getRentDueLabel(record, record.unitDueDate ?? record.roomDueDate ?? 1)}
                                </span>
                              )}
                              <RentStatusBadge status={status as "PENDING" | "PAID" | "AWAITING_VERIFICATION" | "OVERDUE"} />
                            </div>
                          </td>
                          <td className="py-3 text-right">
                            {record.status === "PENDING" && (
                              <button
                                onClick={() =>
                                  setMarkPaidModal({
                                    rentId: record.id,
                                    month: record.month,
                                    year: record.year,
                                  })
                                }
                                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-500"
                              >
                                <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                Mark paid
                              </button>
                            )}
                            {record.status === "PAID" && record.paidDate && (
                              <span className="text-slate-400 dark:text-slate-500 text-xs">
                                Paid {new Date(record.paidDate).toLocaleDateString("en-IN")}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      )}

      {/* Expenses – only when property has units */}
      {units.length > 0 && (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <Typography variant="h2">Expenses</Typography>
            <Link
              to={`/property/${property.id}/expenses/new`}
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              Add expense
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {expenses.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400 py-4">No expenses recorded yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-700">
              {expenses.map((expense) => (
                <li key={expense.id} className="flex items-center justify-between gap-4 py-4 first:pt-0 min-h-[52px]">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">
                      {expense.category}
                      {(expense.unitAlias ?? expense.unitNumber) && (
                        <span className="ml-1.5 text-slate-500 dark:text-slate-400 font-normal text-xs">
                          · {expense.unitAlias ?? expense.unitNumber}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {new Date(expense.date).toLocaleDateString("en-IN")}
                    </p>
                  </div>
                  <p className="font-semibold text-slate-900 dark:text-slate-100">
                    ₹{expense.amount.toLocaleString("en-IN")}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      )}

      {/* Mark paid modal */}
      <Modal
        isOpen={!!markPaidModal}
        onClose={() => setMarkPaidModal(null)}
        title={markPaidModal ? `Mark ${MONTHS[markPaidModal.month - 1]} ${markPaidModal.year} as paid` : undefined}
      >
        {markPaidModal && (
          <div className="space-y-4">
            <div>
              <Select
                label="Payment mode"
                options={PAYMENT_MODES.map((mode) => ({ value: mode, label: mode.replace("_", " ") }))}
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value as typeof PAYMENT_MODES[number])}
              />
            </div>
            <Input
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reference ID, etc."
            />
            <div className="flex gap-3 pt-2">
              <Button variant="secondary" onClick={() => setMarkPaidModal(null)}>
                Cancel
              </Button>
              <Button onClick={handleMarkPaid} isLoading={submitting}>
                Mark as paid
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Add rent record modal (back date) */}
      <Modal
        isOpen={addRentModal}
        onClose={() => setAddRentModal(false)}
        title="Add rent record"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Create a rent record for an existing unit for a past or future month. Useful when you add a unit later and need to add earlier months.
          </p>
          <Select
            label="Unit"
            value={addRentUnitId}
            onChange={(e) => setAddRentUnitId(e.target.value)}
            options={units.map((u) => ({ value: u.id, label: `${u.alias} (${u.unitNumber})` }))}
          />
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Month"
              value={addRentMonth}
              onChange={(e) => setAddRentMonth(e.target.value)}
              options={MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))}
            />
            <Input
              label="Year"
              type="number"
              min="2020"
              max="2100"
              value={addRentYear}
              onChange={(e) => setAddRentYear(e.target.value)}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" onClick={() => setAddRentModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!addRentUnitId) {
                  addToast({ type: "error", message: "Please select a unit" });
                  return;
                }
                addRentMutation.mutate({
                  roomId: addRentUnitId,
                  month: parseInt(addRentMonth, 10) || new Date().getMonth() + 1,
                  year: parseInt(addRentYear, 10) || new Date().getFullYear(),
                });
              }}
              isLoading={addRentMutation.isPending}
            >
              Add record
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
}
